import "./GroupCardShared.scss";

import type { FC } from "react";
import { useBoolean } from "usehooks-ts";

import { PageGroupInfoCard } from "components/materials/page/groups/PageGroupInfoCard";
import { JoinGroupButton } from "components/materials/page/groups/buttons/JoinGroupButton";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  pageGroupSelectors,
  pageSelectors,
  selectCurrentSectionId,
  selectCurrentSectionUserId,
  selectEmptyPageGroupCountByPageId,
  selectPageGroupUserForCurrentUserByPageId,
  selectPageGroupUsersByPageGroupId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";

interface StudentSelfSelectCardProps {
  pageId: string;
}

const MAX_EMPTY_GROUPS_ALLOWED = 2;

interface StudentSelfSelectGroupRowProps {
  pageGroupId: string;
  pageGroupIndex: number;
  pageId: string;
  selectedPageGroupId: string | null;
  hasUserPageGroupUser: boolean;
  isFetching: boolean;
  onChangeGroup: (pageGroupId: string) => void;
  onConfirmGroup: () => void;
}

const StudentSelfSelectGroupRow: FC<StudentSelfSelectGroupRowProps> = ({
  pageGroupId,
  pageGroupIndex,
  pageId,
  selectedPageGroupId,
  hasUserPageGroupUser,
  isFetching,
  onChangeGroup,
  onConfirmGroup,
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["max_group_size"]);
  const pageGroupUsers = useListSelector(
    (s) => selectPageGroupUsersByPageGroupId(s, pageGroupId),
    ["is_ready"],
  );

  const isSelected = selectedPageGroupId === pageGroupId;
  const maxGroupSize = page?.max_group_size ?? null;
  const hasReachedMaxTotal = maxGroupSize !== null && pageGroupUsers.length >= maxGroupSize;
  const hasReachedMaxReady =
    maxGroupSize !== null &&
    pageGroupUsers.filter((pageGroupUser) => pageGroupUser.is_ready).length >= maxGroupSize;

  return (
    <PageGroupInfoCard
      isActive={isSelected}
      pageGroupId={pageGroupId}
      pageGroupIndex={pageGroupIndex}
    >
      <JoinGroupButton
        disabled={isSelected ? hasReachedMaxReady : hasReachedMaxTotal || undefined}
        index={pageGroupIndex}
        isLoading={isFetching}
        isSelected={isSelected}
        kind={isSelected ? "success" : hasUserPageGroupUser ? "tertiary" : "accent"}
        onChangeGroup={() => onChangeGroup(pageGroupId)}
        onConfirm={onConfirmGroup}
      />
    </PageGroupInfoCard>
  );
};

export const StudentSelfSelectCard: FC<StudentSelfSelectCardProps> = ({ pageId }) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["locked_at"]);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const { value: isFetching, setTrue: setFetching, setFalse: setNotFetching } = useBoolean(false);

  const pageGroups = useListSelector((s) => pageGroupSelectors.filterByPageId(s, pageId), ["id"]);
  const userPageGroupUser = useObjectSelector(
    (s) => selectPageGroupUserForCurrentUserByPageId(s, pageId),
    ["id", "page_group_id", "section_user_id"],
  );
  const emptyPageGroupCount = useAppSelector((s) => selectEmptyPageGroupCountByPageId(s, pageId));

  // We are striving to find a balance of when to show the Add Group button.
  // We want to avoid too many empty groups, but also to expedite the process
  // of students adding new groups (given the API calls and updates have a lag time, which students complained about).
  // For now we allow up to 2 empty groups and will re-evaluate this logic in the future.
  const shouldShowAddGroup = !page?.locked_at && emptyPageGroupCount < MAX_EMPTY_GROUPS_ALLOWED;

  const onChangeGroup = async (pageGroupId: string) => {
    if (!sectionUserId || !sectionId) {
      return;
    }
    setFetching();
    try {
      if (userPageGroupUser) {
        // If the current user is already in a group, we just need to update their
        // page_group_id when they join or create a new group.
        await storeApi.page_group_users.partial_update({
          id: userPageGroupUser.id,
          page_group_id: pageGroupId,
          section_user_id: sectionUserId,
        });
      } else {
        // If the current user isn't in any group yet (i.e. don't have a pageGroupUser),
        // we need to create a pageGroupUser for the user.
        await storeApi.page_group_users.create({
          page_group_id: pageGroupId,
          section_user_id: sectionUserId,
          section_id: sectionId,
        });
      }
    } catch {
      // storeApi already reports the error and shows a failure toast.
    } finally {
      setNotFetching();
    }
  };

  const onConfirmGroup = async () => {
    if (!userPageGroupUser) return;
    setFetching();

    try {
      await storeApi.page_group_users.partial_update({
        id: userPageGroupUser.id,
        is_ready: true,
      });
    } catch {
      // storeApi already reports the error and shows a failure toast.
    } finally {
      setNotFetching();
    }
  };

  if (!page || !sectionId) {
    return null;
  }
  return (
    <div className="group-card flex w-full flex-col gap-4 rounded-2xl border border-black-tint-90 bg-black-tint-97 px-5 py-4">
      <div>
        <div className="h2 text-black-tint-20">
          {t("student_self_select_card.join_group_below")}
        </div>
        {userPageGroupUser && (
          <div className="body-m text-black-tint-20">
            {t("student_self_select_card.cannot_change_group_after_start")}
          </div>
        )}
      </div>

      {page.locked_at ? (
        <Banner kind="info">{t("lock_status.group_card_lock_warning")}</Banner>
      ) : (
        pageGroups.map((pageGroup, index) => (
          <StudentSelfSelectGroupRow
            hasUserPageGroupUser={!!userPageGroupUser}
            isFetching={isFetching}
            key={pageGroup.id}
            pageGroupId={pageGroup.id}
            pageGroupIndex={index}
            pageId={pageId}
            selectedPageGroupId={userPageGroupUser?.page_group_id ?? null}
            onChangeGroup={onChangeGroup}
            onConfirmGroup={onConfirmGroup}
          />
        ))
      )}

      {shouldShowAddGroup && (
        <SquareButton
          kind="ghost"
          size="s"
          onClick={() => {
            storeApi.page_groups
              .create({
                page_id: pageId,
                section_id: sectionId,
              })
              .then((res) => {
                onChangeGroup(res.id);
              });
          }}
        >
          <IconText iconStart={<AddPlusIcon />} text={t("common.add.page_group")} />
        </SquareButton>
      )}
    </div>
  );
};
